import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  ChatBubbleOvalLeftEllipsisIcon, 
  DocumentTextIcon, 
  DevicePhoneMobileIcon, 
  ComputerDesktopIcon, 
  GlobeAltIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  NewspaperIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { getAllMessages } from '../../firebase/messageService';
import { getAllAnalyticsData, AnalyticsData } from '../../firebase/analyticsService';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsView: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [messageStats, setMessageStats] = useState({
    total: 0,
    unread: 0,
    replied: 0,
    important: 0,
  });
  const [period, setPeriod] = useState<'7days' | '30days' | '90days' | 'year'>('30days');
  
  // State for analytics data with explicit type annotations to match AnalyticsData
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    visitorStats: {
      visitors: [] as number[],
      pageViews: [] as number[],
      labels: [] as string[]
    },
    deviceData: {
      desktop: 0,
      mobile: 0,
      tablet: 0
    },
    pageVisitsData: {
      labels: [] as string[],
      data: [] as number[]
    },
    geographyData: {
      labels: [] as string[],
      data: [] as number[]
    },
    contentEngagementData: {
      labels: [] as string[],
      data: [] as number[]
    },
    bounceRateData: {
      returning: 0,
      new: 0,
      bounce: 0
    }
  });

  // Prepare chart data from state
  const visitorData = {
    labels: analyticsData.visitorStats.labels,
    datasets: [
      {
        label: 'Visitors',
        data: analyticsData.visitorStats.visitors,
        borderColor: 'rgba(99, 102, 241, 1)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.3,
        fill: true,
      },
      {
        label: 'Page Views',
        data: analyticsData.visitorStats.pageViews,
        borderColor: 'rgba(16, 185, 129, 1)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.3,
        fill: true,
      }
    ]
  };

  const deviceData = {
    labels: ['Desktop', 'Mobile', 'Tablet'],
    datasets: [
      {
        label: 'Device Usage',
        data: [
          analyticsData.deviceData.desktop,
          analyticsData.deviceData.mobile,
          analyticsData.deviceData.tablet
        ],
        backgroundColor: [
          'rgba(99, 102, 241, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)'
        ],
        borderColor: [
          'rgba(99, 102, 241, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)'
        ],
        borderWidth: 1,
      }
    ]
  };
  
  const pageVisitsData = {
    labels: analyticsData.pageVisitsData.labels,
    datasets: [
      {
        label: 'Page Visits',
        data: analyticsData.pageVisitsData.data,
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
      }
    ]
  };

  const contentEngagementData = {
    labels: analyticsData.contentEngagementData.labels,
    datasets: [
      {
        label: 'Content Engagement Score',
        data: analyticsData.contentEngagementData.data,
        backgroundColor: [
          'rgba(99, 102, 241, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(236, 72, 153, 0.7)',
        ],
        borderColor: [
          'rgba(99, 102, 241, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(236, 72, 153, 1)',
        ],
        borderWidth: 1,
      }
    ]
  };

  const geographyData = {
    labels: analyticsData.geographyData.labels,
    datasets: [
      {
        label: 'Visitors by Country',
        data: analyticsData.geographyData.data,
        backgroundColor: [
          'rgba(99, 102, 241, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(236, 72, 153, 0.7)',
          'rgba(14, 165, 233, 0.7)',
          'rgba(168, 85, 247, 0.7)',
          'rgba(156, 163, 175, 0.7)',
        ],
        borderWidth: 1,
      }
    ]
  };

  const bounceRateData = {
    labels: ['Returning Visitors', 'New Visitors', 'Bounce'],
    datasets: [
      {
        label: 'Visitor Engagement',
        data: [
          analyticsData.bounceRateData.returning,
          analyticsData.bounceRateData.new,
          analyticsData.bounceRateData.bounce
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.7)',
          'rgba(99, 102, 241, 0.7)',
          'rgba(239, 68, 68, 0.7)',
        ],
        borderColor: [
          'rgba(16, 185, 129, 1)',
          'rgba(99, 102, 241, 1)',
          'rgba(239, 68, 68, 1)',
        ],
        borderWidth: 1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch message stats
        const messages = await getAllMessages();
        setMessageStats({
          total: messages.length,
          unread: messages.filter(msg => !msg.read).length,
          replied: messages.filter(msg => msg.replied).length,
          important: messages.filter(msg => msg.important).length,
        });
        
        // Fetch analytics data
        const data = await getAllAnalyticsData(period);
        setAnalyticsData(data);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period]);

  const StatsCard = ({ title, value, icon, color }: { title: string, value: number | string, icon: React.ReactNode, color: string }) => (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
      <div className="flex items-center mb-4">
        <div className={`p-3 rounded-full ${color} text-white mr-4`}>{icon}</div>
        <h3 className="text-lg font-medium">{title}</h3>
      </div>
      <p className="text-3xl font-bold">{value}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-2">Analytics Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Visitor statistics and engagement metrics for your portfolio website.
        </p>
        
        {/* Time period selector */}
        <div className="flex space-x-2 mb-6">
          <button 
            onClick={() => setPeriod('7days')}
            className={`px-4 py-2 rounded-md text-sm ${
              period === '7days' 
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' 
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            Last 7 days
          </button>
          <button 
            onClick={() => setPeriod('30days')}
            className={`px-4 py-2 rounded-md text-sm ${
              period === '30days' 
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' 
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            Last 30 days
          </button>
          <button 
            onClick={() => setPeriod('90days')}
            className={`px-4 py-2 rounded-md text-sm ${
              period === '90days' 
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' 
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            Last 90 days
          </button>
          <button 
            onClick={() => setPeriod('year')}
            className={`px-4 py-2 rounded-md text-sm ${
              period === 'year' 
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400' 
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            Last year
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard 
          title="Total Messages" 
          value={messageStats.total} 
          icon={<ChatBubbleOvalLeftEllipsisIcon className="h-6 w-6" />} 
          color="bg-blue-500"
        />
        <StatsCard 
          title="Unread Messages" 
          value={messageStats.unread} 
          icon={<DocumentTextIcon className="h-6 w-6" />} 
          color="bg-red-500"
        />
        <StatsCard 
          title="Total Visitors" 
          value={analyticsData.visitorStats.visitors.reduce((sum: number, val: number) => sum + val, 0)} 
          icon={<EyeIcon className="h-6 w-6" />} 
          color="bg-green-500"
        />
        <StatsCard 
          title="Total Page Views" 
          value={analyticsData.visitorStats.pageViews.reduce((sum: number, val: number) => sum + val, 0)} 
          icon={<ChartBarIcon className="h-6 w-6" />} 
          color="bg-purple-500"
        />
      </div>

      {/* Visitors Chart */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-4">Visitor Traffic</h2>
        <div className="h-80">
          <Line options={chartOptions} data={visitorData} />
        </div>
      </div>

      {/* Two Column Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Usage */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4">Device Usage</h2>
          <div className="h-64 flex items-center justify-center">
            <Doughnut data={deviceData} options={chartOptions} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="flex items-center justify-center mb-2">
                <ComputerDesktopIcon className="h-6 w-6 text-indigo-500 mr-2" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Desktop</p>
              <p className="text-lg font-bold">{analyticsData.deviceData.desktop}</p>
            </div>
            <div>
              <div className="flex items-center justify-center mb-2">
                <DevicePhoneMobileIcon className="h-6 w-6 text-green-500 mr-2" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Mobile</p>
              <p className="text-lg font-bold">{analyticsData.deviceData.mobile}</p>
            </div>
            <div>
              <div className="flex items-center justify-center mb-2">
                <DevicePhoneMobileIcon className="h-6 w-6 text-amber-500 mr-2" transform="rotate(90)" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tablet</p>
              <p className="text-lg font-bold">{analyticsData.deviceData.tablet}</p>
            </div>
          </div>
        </div>

        {/* Geographic Distribution */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4">Geographic Distribution</h2>
          <div className="h-64 flex items-center justify-center">
            <Pie data={geographyData} options={chartOptions} />
          </div>
          <div className="mt-4 flex justify-center">
            <div className="flex items-center">
              <GlobeAltIcon className="h-6 w-6 text-indigo-500 mr-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Visitors from {analyticsData.geographyData.labels.length} countries
              </p>
            </div>
          </div>
        </div>

        {/* Page Visits */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4">Most Visited Pages</h2>
          <div className="h-64">
            <Bar 
              options={{
                ...chartOptions,
                indexAxis: 'y' as const,
              }} 
              data={pageVisitsData} 
            />
          </div>
        </div>

        {/* Visitor Engagement */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold mb-4">Visitor Engagement</h2>
          <div className="h-64 flex items-center justify-center">
            <Doughnut data={bounceRateData} options={chartOptions} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Returning</p>
              <p className="text-lg font-bold">{analyticsData.bounceRateData.returning}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">New</p>
              <p className="text-lg font-bold">{analyticsData.bounceRateData.new}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Bounce</p>
              <p className="text-lg font-bold">{analyticsData.bounceRateData.bounce}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Engagement */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-4">Content Engagement</h2>
        <div className="h-64">
          <Bar options={chartOptions} data={contentEngagementData} />
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex flex-col items-center">
            <BriefcaseIcon className="h-6 w-6 text-indigo-500 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Projects</p>
          </div>
          <div className="flex flex-col items-center">
            <BriefcaseIcon className="h-6 w-6 text-green-500 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Experience</p>
          </div>
          <div className="flex flex-col items-center">
            <AcademicCapIcon className="h-6 w-6 text-amber-500 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Education</p>
          </div>
          <div className="flex flex-col items-center">
            <NewspaperIcon className="h-6 w-6 text-pink-500 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Publications</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView; 